import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getTaxYearOptions } from '../utils/taxYears';
import './TaxYearTracker.css';

const TaxYearTracker = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [rentalIncome, setRentalIncome] = useState({});
  const [deductions, setDeductions] = useState({});
  const [resetting, setResetting] = useState(false);
  const [savingsPlan, setSavingsPlan] = useState({
    quarterly: 0,
    semiAnnual: 0,
    yearly: 0,
    vacation: 0,
    medical: 0,
    houseRepairs: 0,
    totalMonthly: 0
  });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load data on component mount
  useEffect(() => {
    loadTaxYearData();
    loadProperties();
  }, [currentYear]);

  const loadTaxYearData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/tax-year/${currentYear}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setRentalIncome(response.data.rentalIncome || {});
        setDeductions(response.data.deductions || {});
        setSavingsPlan(response.data.savingsPlan || savingsPlan);
      }
    } catch (error) {
      console.error('Error loading tax year data:', error);
      toast.error('Failed to load tax year data');
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success && response.data.data) {
        setProperties(response.data.data);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const getCurrentMonth = () => {
    return new Date().toLocaleString('default', { month: 'long' });
  };

  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  const isCurrentMonth = (month) => {
    return month === getCurrentMonth() && currentYear === getCurrentYear();
  };

  const calculateTotalRentalIncome = () => {
    let total = 0;
    Object.values(rentalIncome).forEach(propertyIncome => {
      Object.values(propertyIncome).forEach(amount => {
        total += amount;
      });
    });
    return total;
  };

  const calculateTotalDeductions = () => {
    let total = 0;
    Object.values(deductions).forEach(propertyDeductions => {
      Object.values(propertyDeductions).forEach(categoryTotal => {
        total += categoryTotal;
      });
    });
    return total;
  };

  const calculateRequiredMonthlySavings = () => {
    const quarterlyMonthly = savingsPlan.quarterly / 3;
    const semiAnnualMonthly = savingsPlan.semiAnnual / 6;
    const yearlyMonthly = savingsPlan.yearly / 12;
    const vacationMonthly = savingsPlan.vacation / 12;
    const medicalMonthly = savingsPlan.medical / 12;
    const houseRepairsMonthly = savingsPlan.houseRepairs / 12;
    
    return quarterlyMonthly + semiAnnualMonthly + yearlyMonthly + vacationMonthly + medicalMonthly + houseRepairsMonthly;
  };

  const handleResetAll = async () => {
    if (!window.confirm('This will permanently erase ALL tax year tracker data: rental income, deductions, expenses, savings plans, and taxable income. You will need to re-import statements. Continue?')) {
      return;
    }
    setResetting(true);
    try {
      await axios.post('/api/tax-year/reset', {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      toast.success('All data erased. You can start fresh with statement imports.');
      setRentalIncome({});
      setDeductions({});
      setSavingsPlan({
        quarterly: 0,
        semiAnnual: 0,
        yearly: 0,
        vacation: 0,
        medical: 0,
        houseRepairs: 0,
        totalMonthly: 0
      });
      loadTaxYearData();
    } catch (error) {
      console.error('Reset error:', error);
      toast.error(error.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const updateSavingsPlan = async (field, value) => {
    const updatedPlan = { ...savingsPlan, [field]: parseFloat(value) || 0 };
    setSavingsPlan(updatedPlan);
    
    try {
      await axios.post('/api/savings-plan', {
        year: currentYear,
        savingsPlan: updatedPlan
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      toast.success('Savings plan updated');
    } catch (error) {
      console.error('Error updating savings plan:', error);
      toast.error('Failed to update savings plan');
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="tax-year-tracker">
        <div className="loading">Loading tax year data...</div>
      </div>
    );
  }

  return (
    <div className="tax-year-tracker">
      <div className="tracker-header">
        <h2>Tax Year {currentYear} Tracker</h2>
        <div className="tracker-header-actions">
          <button
            type="button"
            onClick={handleResetAll}
            disabled={resetting}
            className="reset-all-btn"
            title="Erase all saved data and start over"
          >
            {resetting ? 'Resetting...' : 'Reset All / Start Over'}
          </button>
          <div className="year-selector">
          <label className="year-label">Tax Year:</label>
          <select
            value={currentYear}
            onChange={(e) => setCurrentYear(parseInt(e.target.value, 10))}
            className="year-select"
          >
            {getTaxYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        </div>
      </div>

      {/* Rental Income Section */}
      <div className="section">
        <h3>🏠 Rental Income by Property & Month</h3>
        <div className="rental-income-grid">
          {properties.map(property => (
            <div key={property.id} className="property-income-card">
              <h4>{property.name}</h4>
              <div className="monthly-income">
                {months.map(month => {
                  const amount = rentalIncome[property.id]?.[month] || 0;
                  const isCurrent = isCurrentMonth(month);
                  return (
                    <div 
                      key={month} 
                      className={`income-month ${isCurrent ? 'current-month' : ''}`}
                    >
                      <span className="month-name">{month.substring(0, 3)}</span>
                      <span className="amount">${amount.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="property-total">
                Total: ${Object.values(rentalIncome[property.id] || {}).reduce((sum, amount) => sum + amount, 0).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
        <div className="grand-total">
          <strong>Total Rental Income: ${calculateTotalRentalIncome().toFixed(2)}</strong>
        </div>
      </div>

      {/* Deductions Section */}
      <div className="section">
        <h3>📋 Annual Deductions by Property</h3>
        <div className="deductions-grid">
          {properties.map(property => (
            <div key={property.id} className="property-deductions-card">
              <h4>{property.name}</h4>
              <div className="deduction-categories">
                {Object.entries(deductions[property.id] || {}).map(([category, amount]) => (
                  <div key={category} className="deduction-item">
                    <span className="category">{category}</span>
                    <span className="amount">${amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="property-total">
                Total: ${Object.values(deductions[property.id] || {}).reduce((sum, amount) => sum + amount, 0).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
        <div className="grand-total">
          <strong>Total Deductions: ${calculateTotalDeductions().toFixed(2)}</strong>
        </div>
      </div>

      {/* Savings Planning Section */}
      <div className="section">
        <h3>💰 Marcus Savings Planning</h3>
        <div className="savings-plan-grid">
          <div className="savings-category">
            <label>Quarterly Expenses</label>
            <input
              type="number"
              value={savingsPlan.quarterly}
              onChange={(e) => updateSavingsPlan('quarterly', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.quarterly / 3).toFixed(2)}/month</span>
          </div>
          
          <div className="savings-category">
            <label>Semi-Annual Expenses</label>
            <input
              type="number"
              value={savingsPlan.semiAnnual}
              onChange={(e) => updateSavingsPlan('semiAnnual', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.semiAnnual / 6).toFixed(2)}/month</span>
          </div>
          
          <div className="savings-category">
            <label>Yearly Expenses</label>
            <input
              type="number"
              value={savingsPlan.yearly}
              onChange={(e) => updateSavingsPlan('yearly', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.yearly / 12).toFixed(2)}/month</span>
          </div>
          
          <div className="savings-category">
            <label>Vacation Fund</label>
            <input
              type="number"
              value={savingsPlan.vacation}
              onChange={(e) => updateSavingsPlan('vacation', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.vacation / 12).toFixed(2)}/month</span>
          </div>
          
          <div className="savings-category">
            <label>Medical Expenses</label>
            <input
              type="number"
              value={savingsPlan.medical}
              onChange={(e) => updateSavingsPlan('medical', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.medical / 12).toFixed(2)}/month</span>
          </div>
          
          <div className="savings-category">
            <label>House Repairs (Rentals)</label>
            <input
              type="number"
              value={savingsPlan.houseRepairs}
              onChange={(e) => updateSavingsPlan('houseRepairs', e.target.value)}
              placeholder="0.00"
            />
            <span className="monthly-breakdown">${(savingsPlan.houseRepairs / 12).toFixed(2)}/month</span>
          </div>
        </div>
        
        <div className="savings-summary">
          <div className="required-monthly">
            <strong>Required Monthly Savings: ${calculateRequiredMonthlySavings().toFixed(2)}</strong>
          </div>
          <div className="current-monthly">
            <label>Current Monthly Savings to Marcus:</label>
            <input
              type="number"
              value={savingsPlan.totalMonthly}
              onChange={(e) => updateSavingsPlan('totalMonthly', e.target.value)}
              placeholder="0.00"
              className="current-savings-input"
            />
          </div>
          <div className={`savings-status ${savingsPlan.totalMonthly >= calculateRequiredMonthlySavings() ? 'sufficient' : 'insufficient'}`}>
            {savingsPlan.totalMonthly >= calculateRequiredMonthlySavings() 
              ? '✅ Savings plan is sufficient' 
              : '⚠️ Need to increase monthly savings'
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxYearTracker;


